import { sectionRenderer, reinitSlideshowsWithin } from '@theme/section-renderer';

const BAR_SCROLL_SELECTOR = '.product-list-collection-bar--scroll';

/**
 * Toggle the horizontal edge-fade on a collection bar based on its scroll position.
 * Left fade appears only once scrolled away from the start; right fade hides at the end.
 * @param {Element} scroller
 */
function updateBarFade(scroller) {
  if (!(scroller instanceof HTMLElement)) return;

  const fade = parseFloat(getComputedStyle(scroller).getPropertyValue('--bar-fade-size')) || 28;
  const maxScroll = scroller.scrollWidth - scroller.clientWidth;
  const x = scroller.scrollLeft;

  const start = x > 1 ? fade : 0;
  const end = maxScroll > 1 && x < maxScroll - 1 ? fade : 0;

  scroller.style.setProperty('--bar-fade-start', `${start}px`);
  scroller.style.setProperty('--bar-fade-end', `${end}px`);
}

/**
 * Recompute the fade for every collection bar within a root.
 * @param {ParentNode} [root]
 */
function updateAllBarFades(root = document) {
  root.querySelectorAll(BAR_SCROLL_SELECTOR).forEach(updateBarFade);
}

/**
 * Product list collection bar - handles switching between collections via AJAX.
 * Fetches the collection page URL so the section renders with the correct collection context.
 * Uses event delegation so handlers persist after section content is morphed.
 */
function initProductListCollectionBars() {
  // Scroll doesn't bubble, so listen in the capture phase to catch every bar (survives morphs).
  document.addEventListener(
    'scroll',
    (event) => {
      const target = event.target;
      if (target instanceof Element && target.matches(BAR_SCROLL_SELECTOR)) {
        updateBarFade(target);
      }
    },
    true
  );

  window.addEventListener('resize', () => updateAllBarFades());

  updateAllBarFades();

  document.addEventListener('click', async (event) => {
    const tab = event.target.closest('[data-product-list-tab]');
    if (!tab) return;

    const section = tab.closest('[data-product-list-section][data-collections]');
    if (!section) return;

    const sectionId = section.dataset.sectionId;
    const handle = tab.dataset.collectionHandle;
    if (!sectionId || !handle) return;

    if (tab.getAttribute('aria-selected') === 'true') return;

    const collectionUrl = new URL(`/collections/${handle}`, window.location.origin);

    await sectionRenderer.renderSection(sectionId, {
      cache: false,
      url: collectionUrl,
    });

    const sectionElement = document.getElementById(
      sectionId.startsWith('shopify-section-') ? sectionId : `shopify-section-${sectionId}`
    );
    if (sectionElement) {
      reinitSlideshowsWithin(sectionElement);
      updateAllBarFades(sectionElement);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductListCollectionBars);
} else {
  initProductListCollectionBars();
}
